import { Outlet, NavLink, type NavLinkRenderProps } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Container,
} from '@license-management/ui';
import {
  LayoutDashboard,
  Key,
  Monitor,
  Download,
  ChevronDown,
  LogOut,
  User,
} from 'lucide-react';

function Layout() {
  const { user, customer, logout } = useAuth();

  const navItems = [
    { to: '/', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { to: '/licenses', label: 'Licenses', icon: Key },
    { to: '/activations', label: 'Activations', icon: Monitor },
    { to: '/downloads', label: 'Downloads', icon: Download },
  ];

  return (
    <div className="min-h-screen bg-zinc-50 bg-mesh">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-zinc-200/80">
        <Container>
          <div className="flex justify-between items-center h-16">
            {/* Logo and Company Name */}
            <div className="flex items-center gap-3">
              <img
                src="/icon.png"
                alt="Nordic Graphic Aid"
                className="h-9 w-auto object-contain"
              />
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-zinc-900 tracking-tight">
                  Nordic Graphic Aid
                </span>
                {customer && (
                  <span className="text-xs text-zinc-500">{customer.name}</span>
                )}
              </div>
            </div>

            {/* Navigation - Desktop */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.exact}
                    className={({ isActive }: NavLinkRenderProps) =>
                      `group relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? 'text-primary-700'
                          : 'text-zinc-600 hover:text-zinc-900'
                      }`
                    }
                  >
                    {({ isActive }: NavLinkRenderProps) => (
                      <>
                        <Icon
                          className={`h-4 w-4 transition-transform duration-200 group-hover:scale-110 ${
                            isActive ? 'text-primary-600' : ''
                          }`}
                        />
                        <span>{item.label}</span>
                        {/* Animated underline */}
                        <span
                          className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full transition-all duration-300 ${
                            isActive ? 'w-8' : 'w-0 group-hover:w-4'
                          }`}
                        />
                      </>
                    )}
                  </NavLink>
                );
              })}
            </nav>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-zinc-700 hover:bg-zinc-100/80 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-zinc-100 to-zinc-200 text-zinc-600 border border-zinc-200/50">
                    <User className="h-4 w-4" />
                  </div>
                  <span className="hidden sm:inline-block max-w-[150px] truncate font-medium">
                    {user?.email}
                  </span>
                  <ChevronDown className="h-4 w-4 text-zinc-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="text-sm font-semibold text-zinc-900 truncate">
                    {user?.name || user?.email}
                  </p>
                  <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => logout()}
                  variant="danger"
                  className="cursor-pointer"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </Container>

        {/* Mobile Navigation */}
        <nav className="md:hidden border-t border-zinc-100">
          <Container>
            <div className="flex items-center gap-1 py-2 overflow-x-auto scrollbar-hide">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.exact}
                    className={({ isActive }: NavLinkRenderProps) =>
                      `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                        isActive
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100'
                      }`
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </NavLink>
                );
              })}
            </div>
          </Container>
        </nav>
      </header>

      {/* Main Content */}
      <main className="py-8">
        <Container>
          <Outlet />
        </Container>
      </main>
    </div>
  );
}

export default Layout;
